export type ShippingConfigType = 'pickpoint' | 'store_shipping' | 'technician_shipping' | 'customer_shipping';
export type ShipmentMethod = 'parcel' | 'pallet' | 'courier' | 'pickup';
export type PackageSpeed = 'standard' | 'express';

/**
 * Steuert, wie mit den Artikeln beim Versand verfahren wird:
 * - `technician_transfer`: Artikel werden auf den Ziel-Techniker umgebucht
 *   (Default fuer PickPoint, Techniker- und Filialversand mit Umbuchung)
 * - `consumed`: Artikel werden als verbraucht markiert statt umgebucht
 *   (Default/Zwang fuer Kundenversand; optional fuer Filialversand)
 *
 * Der konkrete consumed_reason (RETURNED/INSTALLED) wird in der LagerApp
 * aus dem Empfaengertyp abgeleitet.
 */
export type ArticleDisposition = 'technician_transfer' | 'consumed';

export interface PickpointOpening {
  day: number;   // 0=Mo .. 6=So
  open: string;  // "HH:MM"
  close: string; // "HH:MM"
}

export interface PickpointStation {
  id: number;
  pp_id: string;
  store_id?: number | null;
  name: string;
  address_company?: string;
  address_street?: string;
  address_street_no?: string;
  address_zip?: string;
  address_city?: string;
  address_state?: string;
  address_country?: string;
  address_latitude?: number;
  address_longitude?: number;
  opening_hours?: string;
  openings?: PickpointOpening[];
  report_type?: string;
  activation_date?: string;
  cancellation_date?: string;
  active: boolean;
  notes?: string;
}

export interface PickpointStationShort {
  id: number;
  pp_id: string;
  name: string;
  address_street?: string;
  address_street_no?: string;
  address_zip?: string;
  address_city?: string;
  address_country?: string;
  active: boolean;
  // Optional angereichert vom ShippingConfig-Hash (fuer die Abholzeit-
  // Validierung im Versandconfig-Form). short_hash selbst bleibt schlank.
  openings?: PickpointOpening[];
  activation_date?: string;
  cancellation_date?: string;
}

export interface ShippingConfig {
  id: number;
  operation_id: number;
  shipping_type: ShippingConfigType;
  shipping_type_label?: string;
  shipment_method?: ShipmentMethod;
  package_speed?: PackageSpeed;
  article_disposition?: ArticleDisposition;
  /**
   * PM-Code (Pickpoint-Master) — Pflichtfeld bei PickPoint-Versand. Wird auf
   * einem Zusatz-Etikett (Code128 + Klartext) aufs Paket gedruckt; die
   * Station verifiziert damit die Abholberechtigung.
   */
  pm_code?: string;
  /**
   * Zeitpunkt, an dem der Versand abgeschlossen wurde. Wenn gesetzt, ist die
   * Config archiviert (Lieferscheine ueber delivery_notes verknuepft) und die
   * Operation aus der Material-Versand-Liste verschwunden. Eine neue aktive
   * Config kann ueber den Endpoint `POST /api/v1/shipping_configs` angelegt
   * werden, um Nachversand zu konfigurieren.
   */
  shipped_at?: string;
  /** Alle Lieferscheine, die beim Versand-Abschluss verknuepft wurden
   *  (1+; Multi-Paket-Versand erzeugt mehrere). */
  delivery_notes?: Array<{ id: number; number?: string }>;
  // Gewuenschter Versandtag als ISO-Datum (YYYY-MM-DD). Wenn leer, gilt das
  // operation_date als Versanddatum.
  shipping_date?: string;
  /**
   * Geplanter Abholzeitpunkt am PickPoint-Store (ISO-8601 Datetime). Default
   * beim Anlegen einer PickPoint-Config: operation_date - 1h. Beim ersten
   * PickpointShipment wird daraus die operation_date der automatisch
   * erzeugten Abhol-Operation. Aenderungen im Buero ziehen die bestehende
   * Pickup-Operation nach.
   */
  pickup_date?: string | null;
  /** ID der automatisch erzeugten Abhol-Operation (null bis erstes Shipment). */
  pickup_operation_id?: number | null;
  /** Ticket des Abholauftrags — fürs Verlinken im Config-Formular (Route /ticket/:id). */
  pickup_ticket_id?: number | null;
  /**
   * Frueheste-Versand-Zeitpunkt fuer Staging-Pakete (ISO-8601 Datetime) oder
   * null. Wenn gesetzt und in der Zukunft, werden die zugehoerigen Staging-
   * Sendungen in der LagerApp zwar vorbereitet (Label/Lieferschein), aber erst
   * ab diesem Zeitpunkt als "versandt" markierbar (Verzoegerter Versand).
   */
  staging_ship_ready_at?: string | null;
  pickpoint_station_id?: number;
  pickpoint_station?: PickpointStationShort;
  target_store_id?: number;
  target_store?: {
    id: number;
    name: string;
    nr?: string;
    address_street?: string;
    address_zip?: string;
    address_city?: string;
  };
  target_user_id?: number;
  target_user_name?: string;
  /**
   * Regel "Ziel-Techniker vom Auftrag": true = der Techniker wird dynamisch aus
   * dem Auftragstechniker abgeleitet (kein fester target_user_id). Default fuer
   * neu angelegte Configs der Typen PickPoint/Techniker/Filiale-mit-Umbuchung.
   */
  target_user_from_operation?: boolean;
  /**
   * Tatsaechlich gueltiger Ziel-Techniker (fest ODER aus dem Auftrag aufgeloest).
   * null = kein sinnvoller Techniker → LagerApp warnt, Lagerist waehlt. Anzeige
   * (Matrix/LagerApp) nutzt diese Werte statt der Roh-`target_user_*`.
   */
  resolved_target_user_id?: number | null;
  resolved_target_user_name?: string | null;
  /**
   * Offset-Regel "X Werktage vor Auftragsdatum" fuer den normalen Versand bzw.
   * Staging. Gesetzt = Regel-Modus (festes Datum wird ignoriert). null = festes
   * Datum (shipping_date / staging_ship_ready_at).
   */
  shipping_date_offset_days?: number | null;
  staging_ship_offset_days?: number | null;
  /**
   * Tatsaechlich gueltige Termine (festes Datum ODER aus Offset berechnet). Fuer
   * die Anzeige in der Matrix/LagerApp. effective_shipping_date als ISO-Datum,
   * effective_staging_ship_ready_at als ISO-8601 Datetime.
   */
  effective_shipping_date?: string | null;
  effective_staging_ship_ready_at?: string | null;
  target_warehouse_id?: number;
  target_warehouse?: {
    id: number;
    label: string;
  };
  /** Frei gewaehlter Ziel-Kunde fuer Kunden-Rueckversand (v.a. interne Projekte). */
  target_customer_id?: number | null;
  target_customer_name?: string;
  /**
   * Technikerversand-Adresse (nur technician_shipping): 'private' =
   * Privatadresse, 'warehouse' = Lageradresse (Umbuchungslager). null = nicht
   * festgelegt -> LagerApp entscheidet wie bisher.
   */
  technician_ship_target?: 'private' | 'warehouse' | null;
  notes?: string;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
  /**
   * Phase 3 (Multi-Config + Mengen-Split): Artikel-/Mengen-Aufteilung dieser
   * Config. Leeres Array = keine explizite Aufteilung (Legacy/Single-Config:
   * die Config deckt den gesamten Bedarf der Operation ab).
   */
  requirement_split?: Array<{ operation_requirement_id: number; quantity: number }>;
  /**
   * Phase 3 (Per-Zelle-Lock): bereits versandte (`shipped`) bzw. zurueckgehaltene
   * (`held`) Mengen dieser Config je `operation_requirement_id`. VERSAND-Mengen
   * kommen aus den Lieferschein-Artikeln (nur `shipped`), STAGING aus den
   * StagingRecords. In der Matrix-Zelle nicht mehr reduzierbar; bei archivierten
   * Configs die Basis fuer die "voll versandt"-Markierung.
   */
  requirement_committed?: Array<{ operation_requirement_id: number; shipped: number; held: number }>;
  /**
   * Phase 3 (Matrix): true wenn die Config VOLLSTAENDIG abgeschlossen ist —
   * beide Versandkanaele versandt (`shipped_at` UND `staging_shipped_at`). Nur
   * dann ist die ganze Spalte read-only. Nur vom `/matrix`-Endpoint geliefert.
   */
  archived?: boolean;
  /** Staging-Versand-Zeitpunkt (Pendant zu `shipped_at` fuer den Staging-Kanal). */
  staging_shipped_at?: string;
  /**
   * Kanal-Abschluss getrennt: `versand_shipped` = Standardversand raus
   * (VERSAND-Zellen read-only), `staging_shipped` = Staging raus (STAGING-Zellen
   * read-only). Eine Config kann nur einen Kanal versandt haben → "teilweise".
   */
  versand_shipped?: boolean;
  staging_shipped?: boolean;
  /** Abgeleiteter Status: 'open' | 'partial' | 'complete'. */
  shipping_status?: 'open' | 'partial' | 'complete';
  /** Lokalisiertes Label: 'offen' | 'teilweise versandt' | 'vollständig versandt'. */
  shipping_status_label?: string;
  /**
   * Phase 3 (Matrix): geplante Staging-Kalenderwoche aus der KW-Zuordnung der
   * StagingRecords der Operation. Fallback fuer die Header-Anzeige, wenn keine
   * explizite Staging-Versandzeit (`staging_ship_ready_at`) gesetzt ist. Nur vom
   * `/matrix`-Endpoint geliefert.
   */
  staging_kw?: number | null;
}

export interface ShippingConfigContext {
  customer_has_return_address: boolean;
  customer_id?: number;
  customer_name?: string;
  ticket_store_id?: number;
  ticket_store_name?: string;
  ticket_store_nr?: string;
  /** Internes Versandprojekt → beim Kunden-Rueckversand ist ein Ziel-Kunde Pflicht. */
  project_is_internal?: boolean;
  /**
   * Ticket hat die Material-Versand-Prioritaet (priority.material_shipping).
   * Das 14-Tage-vor-Auftragsdatum-Default fuers Versanddatum greift NUR bei
   * externen Aufträgen OHNE diese Prio.
   */
  ticket_has_material_shipping_priority?: boolean;
  /**
   * Auftragsgruppe (internal | external_staging | external_material |
   * external_other | cloned) — bestimmt den Default-Offset fuers Versanddatum.
   */
  order_group?: string;
  /**
   * Default-Vorlaufzeit (Werktage vor Auftragsdatum) fuer den NORMALEN Versand
   * der Auftragsgruppe. Das Popup belegt damit die "X Werktage vorher"-Regel vor.
   */
  default_shipping_offset_days?: number | null;
  /**
   * Interner Materialversand: ueber den Ticketfiliale-Namen aufgeloester
   * Techniker. Das Popup belegt damit beim NEU-Anlegen einen FESTEN Ziel-
   * Techniker vor (statt der Auftrags-Regel). null = kein Match.
   */
  internal_store_technician_id?: number | null;
  internal_store_technician_name?: string | null;
}

/**
 * Datenform, die die `ShippingConfigFormComponent` beim Speichern emittiert.
 * Spiegelt die Controller-Strong-Params. Der Aufrufer ist fuer das HTTP-POST
 * verantwortlich (single-op-API, bulk-API oder beliebiger anderer Endpunkt).
 *
 * Hinweise zu Server-Enforcement:
 * - `target_store_id` fehlt hier bewusst — der Controller leitet es bei
 *   `store_shipping` aus dem Ticket ab.
 * - `target_user_id`/`target_warehouse_id` sind optional, werden vom Controller
 *   bei nicht-passenden Versandtypen ignoriert/genullt.
 */
export interface ShippingConfigFormData {
  shipping_type: ShippingConfigType;
  shipment_method: ShipmentMethod;
  package_speed: PackageSpeed | null;
  article_disposition: ArticleDisposition;
  shipping_date: string | null;
  pickpoint_station_id?: number | null;
  target_user_id?: number | null;
  /** Regel "Ziel-Techniker vom Auftrag" — kein fester target_user_id. */
  target_user_from_operation?: boolean;
  /**
   * Offset-Regel "X Werktage vor Auftragsdatum". Gesetzt = Regel-Modus (festes
   * Datum wird verworfen). null = festes Datum. Immer mitschicken (auch null),
   * damit ein Wechsel zurueck auf festes Datum die Regel loescht.
   */
  shipping_date_offset_days?: number | null;
  staging_ship_offset_days?: number | null;
  target_warehouse_id?: number | null;
  /** Ziel-Kunde fuer Kunden-Rueckversand; bei anderen Versandtypen genullt. */
  target_customer_id?: number | null;
  /**
   * Technikerversand-Adresse (nur technician_shipping): 'private' = Privat-,
   * 'warehouse' = Lageradresse. Controller nullt das Feld bei anderen Typen.
   */
  technician_ship_target?: 'private' | 'warehouse' | null;
  notes?: string;
  /** PM-Code fuer PickPoint-Versand; bei anderen Versandtypen ignoriert. */
  pm_code?: string | null;
  /**
   * Abholzeitpunkt am PickPoint (ISO-8601). Nur bei `shipping_type === 'pickpoint'`
   * relevant — Controller nullt das Feld bei anderen Typen.
   */
  pickup_date?: string | null;
  /**
   * Frueheste-Versand-Zeitpunkt fuer Staging-Pakete (ISO-8601) oder null. Leer
   * = keine Verzoegerung. Steuert den "Verzoegerter Versand"-Flow in der LagerApp.
   */
  staging_ship_ready_at?: string | null;
}

export interface PickpointShipment {
  id: number;
  pickpoint_station_id: number;
  pickpoint_station?: PickpointStationShort;
  delivery_note_id?: number;
  delivery_note_number?: string;
  operation_id?: number;
  status: 'prepared' | 'shipped' | 'delivered' | 'picked_up';
  shipped_at?: string;
  delivered_at?: string;
  picked_up_at?: string;
  articles_count: number;
  staging_records_count: number;
  shipped_by_name?: string;
  notes?: string;
  created_at?: string;
}

// Gemeinsame Seeding-Regel für Split-States (Versand-Matrix + Bulk-Excel,
// hawk#558 Aufgabe C): Versandte VERSAND-Zellen sind read-only und gehören
// NICHT in den editierbaren State — ihre Menge zählt ausschließlich über
// versandShippedConsumed als Untergrenze in den Pool. Würden sie mitgeseedet,
// zählte die versandte Menge doppelt (State + Consumed), der Pool würde bei
// unveränderter Gesamtmenge rechnerisch negativ und der Überhang-Abbau nähme
// offenen Geschwister-Configs fälschlich ihre Mengen weg. Staging-Zellen
// bleiben (auch bei versandtem Staging) als gesperrte Zellen im State.
export function versandCellShipped(config: ShippingConfig, isStagingCell: boolean): boolean {
  return !isStagingCell && !!config.versand_shipped;
}
